import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { BadgeCheck, ExternalLink, FileText, Globe, RefreshCw, ScrollText } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { PERMISSIONS } from '@/lib/permissions'
import { InfoRow } from '@/components/DataDisplay'
import { StatusBadge, Tag } from '@/components/StatusBadge'
import { PermissionDenied, Skeleton } from '@/components/States'
import { useAuth } from '@/providers/AuthProvider'
import { useToast } from '@/providers/UiProviders'
import {
  decideRequest,
  fetchDocuments,
  fetchDomainChecks,
  fetchExternalChecks,
  fetchPendingRequests,
  fetchRequest,
  runDomainCheck,
  runExternalCheck,
  VERIF_STR as S,
  type DomainCheck,
  type ExternalCheck,
  type VerifDoc,
  type VerifRequest,
} from './verifApi'

/**
 * A9.03 + A9.04 + A9.05 + A9.06 — Verifikatsiya ko'rigi (review workbench).
 * Bir ekranда: hujjatlarni ko'rish (A9.03), domen tasdig'i (A9.04), tashqi
 * manba tekshiruvi (A9.05) va qaror — tasdiq/rad (A9.06). Chap = pending navbat,
 * o'ng = tanlangan arizaning to'liq ko'rigi. Live: GET /admin/verification[/:id],
 * POST .../decide. Sub-tekshiruvlar backend kelguncha fail-soft (bo'sh).
 */
export function VerificationReview() {
  const { can } = useAuth()
  const { toast } = useToast()
  const [list, setList] = useState<VerifRequest[] | null>(null)
  const [selId, setSelId] = useState<number | null>(null)
  const [reload, setReload] = useState(0)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setList(null)
    fetchPendingRequests(ac.signal)
      .then((r) => {
        if (!alive) return
        setList(r)
        setSelId((cur) => cur ?? r[0]?.id ?? null)
      })
      .catch(() => alive && setList([]))
    return () => {
      alive = false
      ac.abort()
    }
  }, [reload])

  if (!can(PERMISSIONS.USERS_VIEW)) return <PermissionDenied permission={PERMISSIONS.USERS_VIEW} />

  const kindLabel = (k: string) =>
    k === 'org' ? S.kindOrg : k === 'notable' ? S.kindNotable : S.kindIdentity

  const onDecided = () => {
    setSelId(null)
    setReload((n) => n + 1)
  }

  return (
    <>
      <PageHeader
        title={S.review}
        screenId="A9.03"
        description={S.reviewNote}
        actions={
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setReload((n) => n + 1)}
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <RefreshCw size={13} />
              {S.refresh}
            </button>
            <Link
              to="/verification"
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <BadgeCheck size={13} />
              {S.queue}
            </Link>
          </div>
        }
      />
      <div className="grid min-h-0 flex-1 grid-cols-[280px_1fr] overflow-hidden">
        {/* master — pending navbat */}
        <aside className="min-h-0 overflow-auto border-r border-border-default bg-surface-1">
          {list === null ? (
            <div className="space-y-2 p-3">
              {Array.from({ length: 6 }, (_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : list.length === 0 ? (
            <p className="p-4 text-sm text-tertiary">{S.noPending}</p>
          ) : (
            <ul>
              {list.map((r) => (
                <li key={r.id}>
                  <button
                    onClick={() => setSelId(r.id)}
                    className={`flex w-full flex-col items-start gap-0.5 border-b border-border-subtle px-3 py-2 text-left hover:bg-surface-2 ${
                      selId === r.id ? 'bg-surface-2' : ''
                    }`}
                  >
                    <span className="text-sm text-primary">
                      {r.username ? `@${r.username}` : `#${r.user_id}`}
                    </span>
                    <span className="text-xs text-tertiary">
                      #{r.id} · {kindLabel(r.kind)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        {/* detail — tanlangan arizaning ko'rigi */}
        <section className="min-h-0 overflow-auto p-3">
          {selId === null ? (
            <p className="text-sm text-tertiary">{S.pickRequest}</p>
          ) : (
            <ReviewDetail
              key={selId}
              id={selId}
              canDecide={can(PERMISSIONS.USERS_BAN)}
              kindLabel={kindLabel}
              onDecided={onDecided}
              toast={toast}
            />
          )}
        </section>
      </div>
    </>
  )
}

function ReviewDetail({
  id,
  canDecide,
  kindLabel,
  onDecided,
  toast,
}: {
  id: number
  canDecide: boolean
  kindLabel: (k: string) => string
  onDecided: () => void
  toast: ReturnType<typeof useToast>['toast']
}) {
  const [req, setReq] = useState<VerifRequest | null>(null)
  const [docs, setDocs] = useState<VerifDoc[] | null>(null)
  const [domain, setDomain] = useState<DomainCheck[] | null>(null)
  const [external, setExternal] = useState<ExternalCheck[] | null>(null)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setReq(null)
    setDocs(null)
    setDomain(null)
    setExternal(null)
    fetchRequest(id, ac.signal).then((r) => alive && setReq(r))
    fetchDocuments(id, ac.signal).then((r) => alive && setDocs(r))
    fetchDomainChecks(id, ac.signal).then((r) => alive && setDomain(r))
    fetchExternalChecks(id, ac.signal).then((r) => alive && setExternal(r))
    return () => {
      alive = false
      ac.abort()
    }
  }, [id])

  const decide = useCallback(
    async (approve: boolean) => {
      setBusy(true)
      try {
        await decideRequest(id, approve, note)
        toast(approve ? S.approved : S.rejected, { tone: 'success' })
        onDecided()
      } catch (e) {
        toast(S.failed, {
          tone: 'error',
          ...(e instanceof Error && e.message ? { description: e.message } : {}),
        })
        setBusy(false)
      }
    },
    [id, note, onDecided, toast],
  )

  const recheckDomain = useCallback(async () => {
    const d = domain?.[0]?.domain ?? ''
    const r = await runDomainCheck(id, d)
    toast(r.ok ? S.saved : S.failed, { tone: r.ok ? 'success' : 'error' })
  }, [id, domain, toast])

  const recheckExternal = useCallback(async () => {
    const src = external?.[0]?.source ?? 'gov_registry'
    const r = await runExternalCheck(id, src)
    toast(r.ok ? S.saved : S.failed, { tone: r.ok ? 'success' : 'error' })
  }, [id, external, toast])

  if (req === null) {
    return (
      <div className="max-w-2xl space-y-2 rounded-lg border border-border-default bg-surface-2 p-4">
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} className="h-4 w-2/3" />
        ))}
      </div>
    )
  }

  const statusKind =
    req.status === 'pending' ? 'pending' : req.status === 'approved' ? 'active' : 'deleted'

  return (
    <div className="max-w-2xl space-y-3">
      {/* ariza xulosasi */}
      <Card>
        <div className="mb-3 flex items-center justify-between">
          <span className="text-sm font-medium text-primary">
            #{req.id} · {kindLabel(req.kind)}
          </span>
          <StatusBadge status={statusKind} />
        </div>
        <InfoRow label={S.user}>
          <Link to={`/users/${req.user_id}`} className="text-accent hover:underline">
            {req.username ? `@${req.username}` : `#${req.user_id}`}
          </Link>
        </InfoRow>
        <InfoRow label={S.evidence}>
          <span className="whitespace-pre-wrap">{req.evidence || '—'}</span>
        </InfoRow>
        {req.status !== 'pending' && (
          <InfoRow label={S.decisionNote}>{req.decision_note || '—'}</InfoRow>
        )}
      </Card>

      {/* A9.03 — hujjatlar */}
      <Section icon={<FileText size={14} />} title={S.documents}>
        {docs === null ? (
          <Skeleton className="h-10 w-full" />
        ) : docs.length === 0 ? (
          <Muted>{S.noDocuments}</Muted>
        ) : (
          <div className="flex flex-wrap gap-2">
            {docs.map((d) => (
              <a
                key={d.id}
                href={d.file_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-1 px-2.5 py-1 text-xs text-secondary hover:border-border-strong hover:text-primary"
              >
                <ExternalLink size={12} />
                {d.kind}
                {d.status && <Tag>{d.status}</Tag>}
              </a>
            ))}
          </div>
        )}
      </Section>

      {/* A9.04 — domen tasdig'i */}
      <Section
        icon={<Globe size={14} />}
        title={S.domainCheck}
        action={
          <MiniBtn onClick={recheckDomain} label={S.recheckDomain} />
        }
      >
        {domain === null ? (
          <Skeleton className="h-10 w-full" />
        ) : domain.length === 0 ? (
          <Muted>{S.noDomainChecks}</Muted>
        ) : (
          <div className="space-y-1.5">
            {domain.map((d) => (
              <div key={d.id} className="flex items-center justify-between text-sm">
                <span className="font-mono text-secondary">{d.domain}</span>
                <span className="flex items-center gap-2 text-xs text-tertiary">
                  {d.method} · {d.token}
                  <StatusBadge
                    status={d.status === 'verified' ? 'active' : d.status === 'failed' ? 'deleted' : 'pending'}
                  />
                </span>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* A9.05 — tashqi manba */}
      <Section
        icon={<ScrollText size={14} />}
        title={S.externalCheck}
        action={<MiniBtn onClick={recheckExternal} label={S.recheckExternal} />}
      >
        {external === null ? (
          <Skeleton className="h-10 w-full" />
        ) : external.length === 0 ? (
          <Muted>{S.noExternalChecks}</Muted>
        ) : (
          <div className="space-y-1.5">
            {external.map((x) => (
              <div key={x.id} className="flex items-center justify-between text-sm">
                <span className="text-secondary">
                  {x.source} · <span className="font-mono text-xs">{x.reference}</span>
                </span>
                <StatusBadge
                  status={x.result === 'match' ? 'active' : x.result === 'no_match' ? 'deleted' : 'pending'}
                />
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* A9.06 — qaror */}
      {req.status === 'pending' && canDecide && (
        <Card>
          <div className="mb-2 text-sm font-medium text-secondary">{S.decision}</div>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={S.decisionNote}
            rows={2}
            className="mb-3 w-full rounded-md border border-border-default bg-surface-1 px-2.5 py-1.5 text-sm text-primary"
          />
          <div className="flex gap-2">
            <button
              disabled={busy}
              onClick={() => decide(false)}
              className="rounded-md border border-border-default bg-surface-1 px-3 py-1.5 text-sm text-secondary hover:text-primary disabled:opacity-50"
            >
              {S.reject}
            </button>
            <button
              disabled={busy}
              onClick={() => decide(true)}
              className="rounded-md border border-success bg-success-subtle px-3 py-1.5 text-sm text-success hover:bg-success hover:text-white disabled:opacity-50"
            >
              {S.approve}
            </button>
          </div>
          <p className="mt-2 text-xs text-tertiary">{S.approveHint}</p>
        </Card>
      )}
    </div>
  )
}

function Card({ children }: { children: ReactNode }) {
  return <div className="rounded-lg border border-border-default bg-surface-2 p-4">{children}</div>
}

function Section({
  icon,
  title,
  action,
  children,
}: {
  icon: ReactNode
  title: string
  action?: ReactNode
  children: ReactNode
}) {
  return (
    <div className="rounded-lg border border-border-default bg-surface-2 p-4">
      <div className="mb-2 flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-sm font-medium text-secondary">
          {icon}
          {title}
        </span>
        {action}
      </div>
      {children}
    </div>
  )
}

function Muted({ children }: { children: ReactNode }) {
  return <p className="text-sm text-tertiary">{children}</p>
}

function MiniBtn({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-1 px-2 py-0.5 text-xs text-secondary hover:border-border-strong hover:text-primary"
    >
      <RefreshCw size={11} />
      {label}
    </button>
  )
}
